import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { apiBaseUrl } from '../../util/api';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page, NamedLink, NeedHelpWidget } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import SoldListingCard from './SoldListingCard';
import css from './SellPage.module.css';

const IMG = '/static/images/sell';

const layoutAreas = `
  topbar
  main
  footer
`;

// ---------- Content (copy is taken verbatim from the design) ----------

const STEPS = [
  {
    n: 1,
    img: 'step-1',
    title: 'Create a listing',
    body: [
      'Add details, upload photos and set a price with our seller resources.',
      'Creating a listing is free, only once you sell we keep a small fee of 3%.',
    ],
    link: { name: 'FAQPage', to: { hash: 's-02' } },
  },
  {
    n: 2,
    img: 'step-2',
    title: 'Sell easily',
    body: [
      'Buyers can make offers or buy directly.',
      'Most items successfully sold on Avento received offers or have been bought directly within 3 weeks.',
    ],
    link: { name: 'FAQPage', to: { hash: 's-02' } },
  },
  {
    n: 3,
    img: 'step-3',
    title: 'Ship your gear',
    body: [
      'When someone purchased your item you send it with an established carrier of your choice.',
      'You set the shipping cost in advance.',
    ],
    link: { name: 'TransactionProcessSellerPage' },
  },
  {
    n: 4,
    img: 'step-4',
    title: 'Get your money',
    body: [
      'Once your item has arrived and the buyer has approved it, you get paid directly to your bank account, usually within 2-3 days.',
    ],
    link: { name: 'FAQPage', to: { hash: 's-08' } },
  },
];

// DOM order is the mobile order (Sell quicker first); the desktop grid restores
// "Easy to use, Sell quicker, Safety" via CSS order (see SellPage.module.css).
const BENEFITS = [
  {
    icon: 'icon-quicker.svg',
    title: 'Sell quicker',
    body: [
      'Reach 200+ people looking for skydiving gear on Avento every day. Through our specific filters and search buyers find your listing well.',
      'We promote your listing in our network, socials and paid advertising so you get maximal online visibility for free.',
    ],
  },
  {
    icon: 'icon-easy.svg',
    title: 'Easy to use',
    body: [
      'Creating a listing only takes a few minutes. You can then manage everything in one place. Share the link anywhere while keeping messages and requests in one place.',
    ],
  },
  {
    icon: 'icon-safety.svg',
    title: 'Safety',
    body: [
      'With our escrow payments buyers trust you and you are safe because the money is already paid when you ship.',
      'Once the buyer inspected the item you get paid directly to your bank account.',
    ],
  },
];

// ---------- Schema ----------

function buildPageSchema(rootURL) {
  return {
    '@type': 'WebPage',
    name: 'Sell your used skydiving gear on Avento',
    url: `${rootURL}/sell`,
    description:
      'List on the largest marketplace for skydiving gear and reach thousands of buyers. Create a listing for free and sell your used skydiving gear safely on Avento.',
  };
}

// ---------- Sections ----------

const StepCard = ({ step }) => (
  <div className={css.stepCard}>
    <div className={css.stepImageWrap}>
      {/* Above the fold: eager load. width/height on source+img only set the per-breakpoint
          aspect ratio for layout reservation (no CLS); CSS width:100%/height:auto still sizes
          the rendered image, same pattern as the home hero. */}
      <picture>
        <source
          media="(min-width: 860px)"
          srcSet={`${IMG}/${step.img}-desktop.webp`}
          width={500}
          height={382}
        />
        <img
          src={`${IMG}/${step.img}-mobile.webp`}
          alt=""
          className={css.stepImage}
          width={700}
          height={312}
          fetchpriority={step.n === 1 ? 'high' : undefined}
        />
      </picture>
      <span className={css.stepNumber} aria-hidden="true">
        {step.n}.
      </span>
    </div>
    <div className={css.stepBody}>
      <h3 className={css.stepTitle}>{step.title}</h3>
      {step.body.map((p, i) => (
        <p key={i} className={css.stepText}>
          {p}
        </p>
      ))}
      <NamedLink className={css.learnMore} name={step.link.name} to={step.link.to}>
        Learn more
      </NamedLink>
    </div>
  </div>
);

const BenefitCard = ({ benefit }) => (
  <div className={css.benefitCard} data-scroll-card>
    <img src={`${IMG}/${benefit.icon}`} alt="" className={css.benefitIcon} width={56} height={56} />
    <h3 className={css.benefitTitle}>{benefit.title}</h3>
    <div className={css.benefitBody}>
      {benefit.body.map((p, i) => (
        <p key={i} className={css.benefitText}>
          {p}
        </p>
      ))}
    </div>
  </div>
);

// ---------- Main component ----------

const SellPageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();
  const rootURL = config.marketplaceRootURL || '';

  const [sales, setSales] = useState([]);
  useEffect(() => {
    let cancelled = false;
    fetch(`${apiBaseUrl()}/api/recently-sold`)
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (data && Array.isArray(data.sales) && !cancelled) setSales(data.sales);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Page
      title="Sell your used skydiving gear on Avento"
      description="List on the largest marketplace for skydiving gear and reach thousands of buyers. Create a listing for free and sell your used skydiving gear safely on Avento."
      schema={buildPageSchema(rootURL)}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="SellPage" />
              </Topbar>

              <Main as="main" className={css.main}>
                {/* Hero heading */}
                <section className={css.heroSection}>
                  <div className={css.container}>
                    <h1 className={css.heroTitle}>Sell your used skydiving gear on Avento</h1>
                    <p className={css.heroSubtitle}>
                      List on the largest marketplace for skydiving gear and reach thousands of
                      buyers
                    </p>
                  </div>
                </section>

                {/* Four step cards */}
                <section className={css.stepsSection}>
                  <div className={css.container}>
                    <h2 className={css.visuallyHidden}>How selling on Avento works</h2>
                    <div className={css.stepsGrid}>
                      {STEPS.map(step => (
                        <StepCard key={step.n} step={step} />
                      ))}
                    </div>
                    <div className={css.sellNowWrap}>
                      <NamedLink className={css.sellNowButton} name="NewListingPage">
                        Sell now
                      </NamedLink>
                    </div>
                  </div>
                </section>

                {/* Your benefit */}
                <section className={css.benefitSection}>
                  <div className={css.container}>
                    <h2 className={css.sectionHeading}>Your benefit</h2>
                    <div className={css.benefitGrid}>
                      {BENEFITS.map(benefit => (
                        <BenefitCard key={benefit.title} benefit={benefit} />
                      ))}
                    </div>
                  </div>
                </section>

                {/* Recently sold (real data; section hides when empty) */}
                {sales.length > 0 && (
                  <section className={css.recentSection}>
                    <div className={css.container}>
                      <h2 className={css.sectionHeading}>Recently sold on Avento</h2>
                      <p className={css.sectionSub}>Sell quick and easily</p>
                      <div className={css.recentRow}>
                        {sales.map(sale => (
                          <div key={sale.id} className={css.recentCardWrap} data-scroll-card>
                            <SoldListingCard sale={sale} />
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                )}

                {/* Bottom hero */}
                <section className={css.bottomHero}>
                  <picture>
                    <source
                      media="(min-width: 860px)"
                      srcSet={`${IMG}/sell-banner-desktop.webp`}
                      width={1968}
                      height={697}
                    />
                    <img
                      src={`${IMG}/sell-banner-mobile.webp`}
                      alt=""
                      className={css.bottomHeroImage}
                      width={929}
                      height={697}
                      aria-hidden="true"
                      loading="lazy"
                    />
                  </picture>
                  <div className={css.bottomHeroContent}>
                    <div className={css.container}>
                      <div className={css.bottomHeroInner}>
                        <h2 className={css.bottomHeroTitle}>
                          DON'T JUMP IT?
                          <br />
                          <span className={css.bottomHeroAccent}>SELL IT</span>
                        </h2>
                        <p className={css.bottomHeroSub}>Reach thousands of buyers on Avento</p>
                        <NamedLink className={css.bottomHeroButton} name="NewListingPage">
                          <span>SELL NOW</span>
                          <img
                            src="/static/images/landing/hero-cta-arrow.svg"
                            alt=""
                            className={css.bottomHeroButtonArrow}
                            width={20}
                            height={20}
                          />
                        </NamedLink>
                      </div>
                    </div>
                  </div>
                </section>

                <NeedHelpWidget />
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const SellPage = connect(mapStateToProps)(SellPageComponent);

export default SellPage;
